/**
 * The layering scale (operator-parity phase 21).
 *
 * One source of truth for every z-index in the app. Before this, layering was
 * decided per component by picking a number that beat whatever it last
 * collided with — ~25 distinct values across nine bands, owned by nobody. That
 * is not cosmetic: the phase-20 bug was a form the user could see but not type
 * into.
 *
 * ── Calibrated against MEASURED values, not library defaults ────────────────
 *
 * The first draft of this scale put everything in the 1000s, on the
 * assumption that third-party overlays sit at library defaults. They do not:
 * react-joyride ships at 10000 here. The 99999 popover band exists to clear
 * that, and lowering popovers into the 1000s would bury them ~8,900 points
 * beneath the overlays they render inside.
 *
 * So the bands below sit ABOVE the measured third-party ceiling, not above a
 * documented default.
 *
 * ── Two questions, one number ───────────────────────────────────────────────
 *
 * "Which layer am I?" is answered here. "Am I inside another layer?" is NOT a
 * number — it is a stacking-context question, answered by portaling into the
 * host (see `useCgDialogPortalContainer` / `useCgDrawerPortalContainer`). A
 * descendant of a stacking context can never outrank that context's siblings,
 * however large its z-index: a child at 999999 inside CgDialog's transformed
 * content still loses to a body-level sibling at 10070. Reach for a portal,
 * not a bigger number.
 */

/** Layers this app owns. */
export const LAYER = {
  /** Default flow. */
  base: 0,
  /** Sticky table headers, overlapping cells, chips. */
  raised: 10,
  /** Sidebar, header, persistent nav. */
  chrome: 1000,

  /**
   * Dialog/drawer scrim and content.
   *
   * NOT a single value: this codebase legitimately stacks a confirm over a
   * form dialog, and those overlays are SIBLINGS (rendered after `</CgDialog>`),
   * so no portal can order them — only a number can. `dialogAt(depth)` below
   * assigns each open overlay its own rung.
   */
  dialogBase: 10100,
  /** Gap between one overlay and the next. Wide enough for a hosted popover. */
  dialogStride: 20,
  /** Scrim sits `dialogStride / 2` below its own content. */
  dialogContentOffset: 10,
  /** Overlays beyond this share the top rung; nothing real nests this deep. */
  maxDialogDepth: 8,

  /**
   * Popovers, tooltips and menus anchored to PAGE content.
   *
   * Below `dialogBase` on purpose: a menu left open on the page behind a
   * dialog must not paint over it. A popover that belongs to a dialog portals
   * into that dialog and stacks within it — it does not use this value.
   */
  pagePopover: 10050,

  /**
   * Always-on-top. Above every reachable dialog rung, because a save failure
   * must be readable over the dialog that caused it, and above the react-select
   * portal band (20000/20001) — a tie there would be resolved by DOM order,
   * which is not a decision anyone made.
   *
   * Honest limitation until the popover band migrates: the legacy 99999/100160
   * popovers still outrank this. That is unchanged from before (toast was
   * 10060), so nothing regresses — but a toast can still be covered by an open
   * CgPopover until those move to `pagePopover`.
   */
  /**
   * Guided-tour spotlight and tooltip (react-joyride, diagram editor).
   *
   * Above every dialog rung on purpose: a tour step can point INTO an open
   * dialog — the reporting-period dialog carries a "Guide" button that starts
   * one — and the diagram tour's hand-written 10060 put its tooltip UNDER any
   * CgDialog scrim (10100), which is invisible rather than merely wrong. A
   * dialog that hosts a tour must also be non-modal (`CgDialog modal={false}`),
   * because a modal one sets `body { pointer-events: none }` and the tooltip's
   * own buttons stop responding whatever it is layered above.
   *
   * Below `toast`, which has to stay readable over a tour.
   */
  tour: 10400,

  /**
   * The full-screen diagram opened over a monitoring plan. Above the tour, so
   * a tour left running on the plan never paints over the diagram, and below
   * `toast`, so a save failure stays readable over it.
   */
  diagramOverlay: 10450,

  spotlight: 10500,

  toast: 21000,
  /** Cursor-following tooltips, above everything the app owns. */
  tooltip: 21100,

  /**
   * Full-app blocking waits (the language-switch overlay). Above toast and
   * tooltip: while it is up the app is deliberately non-interactive, so
   * nothing beneath it needs to stay readable. Same honest limitation as
   * `toast` — the legacy 99999 popovers still outrank it until they migrate.
   */
  blockingOverlay: 21200,
};

/**
 * Third-party layers this module does NOT control. Listed so the scale names
 * its neighbours — one that isn't written down gets re-litigated the first
 * time it moves.
 */
export const THIRD_PARTY = {
  /** react-joyride onboarding tour (`App/components/OnboardingTour.jsx`). */
  joyride: 10000,
  /** `@xyflow/react` viewport/nodes (its own stylesheet). */
  reactFlow: 1001,
  /**
   * Chakra's band, reserved. Its only consumer was
   * `@lothemar/react-spreadsheet-import`, removed with the bespoke purchased-
   * goods importer. Nothing in `src` imports Chakra today; the band stays
   * reserved rather than reused, so a future Chakra-based dependency does not
   * land on top of a number somebody has since claimed.
   */
  chakraMin: 1000,
  chakraMax: 1800,
  /**
   * react-select `menuPortal`, set per-styles rather than by the library
   * (`CbamStock/components/stockSelectStyles.js`, `CgCnGoodSelector`). Above
   * dialogs today, so a select menu on the page can cover one — folded into
   * `pagePopover` when the popover band migrates.
   */
  reactSelectMenuPortal: 20001,
};

/** Highest rung a dialog can reach — what `toast` must clear. */
export const MAX_DIALOG_Z =
  LAYER.dialogBase + LAYER.dialogStride * LAYER.maxDialogDepth + LAYER.dialogContentOffset;

/**
 * z-index for the overlay (scrim) of the `depth`-th open dialog, 0-based.
 * Content sits `dialogContentOffset` above its own scrim, leaving room for a
 * popover that must beat this dialog but cannot portal into it.
 */
export const dialogAt = (depth = 0) => {
  const rung = Math.min(Math.max(depth, 0), LAYER.maxDialogDepth);
  const overlay = LAYER.dialogBase + rung * LAYER.dialogStride;
  return { overlay, content: overlay + LAYER.dialogContentOffset };
};

// ── Runtime overlay depth ────────────────────────────────────────────────────
//
// Keyed on OPEN, not mount: a dialog that is mounted-but-closed occupies no
// rung, and a parent closing under an open child must not leave a hole. The
// stack is module-level because overlays are siblings in the tree and cannot
// pass depth to each other through context.

const openOverlays = [];

/** Claim the next rung. Returns the depth taken. */
export const pushOverlay = (token) => {
  if (!openOverlays.includes(token)) openOverlays.push(token);
  return openOverlays.indexOf(token);
};

/** Release a rung. Later overlays keep their relative order. */
export const popOverlay = (token) => {
  const i = openOverlays.indexOf(token);
  if (i !== -1) openOverlays.splice(i, 1);
};

/** Current depth of a token, or -1 when it holds no rung. */
export const overlayDepth = (token) => openOverlays.indexOf(token);

/** Test seam — the stack is module state and leaks between cases otherwise. */
export const resetOverlays = () => {
  openOverlays.length = 0;
};

export default LAYER;
